import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import api from '../../services/api';
import { useToastContext } from '../../context/ToastContext';

const unwrap = (r) => (Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : []);
const isLiveGroup = (g) => g && g.isActive !== false && (g.status || 'active') !== 'completed';

function isSportCenter() {
  try { return localStorage.getItem('learningSubType') === 'sport'; } catch (_) { return false; }
}

/**
 * Pick the group a new client joins, or create one on the spot.
 *
 * Centers set up their groups first and then put every new client straight
 * into one, so the add-client forms carry this picker: a select of live
 * groups, plus "+ New group" that opens a small inline form (name + coach),
 * creates the group and selects it without leaving the modal.
 *
 * Sport centers may create a group with no coach (the backend allows it for
 * sport only); everyone else must pick one.
 *
 * Props: value (groupId | ''), onChange(groupId), disabled, hint (text under the select).
 */
export default function GroupPicker({ value, onChange, disabled = false, hint }) {
  const { t } = useTranslation();
  const { success } = useToastContext();
  const coachOptional = isSportCenter();

  const [groups, setGroups] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([api.get('/groups'), api.get('/teachers')]).then(([g, tr]) => {
      if (cancelled) return;
      setGroups(g.status === 'fulfilled' ? unwrap(g.value).filter(isLiveGroup) : []);
      setTeachers(tr.status === 'fulfilled' ? unwrap(tr.value).filter((x) => x.isActive !== false) : []);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const closeNew = () => { setShowNew(false); setName(''); setTeacherId(''); setError(null); };

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError(t('clientGroup.errName')); return; }
    if (!coachOptional && !teacherId) { setError(t('clientGroup.errTeacher')); return; }
    setError(null);
    setCreating(true);
    try {
      const res = await api.post('/groups', {
        name: trimmed,
        // Same defaults as GroupsPage's create form; pricing is edited on the group later.
        billingMethod: coachOptional ? 'session' : 'hourly',
        ...(teacherId ? { teacherId } : {}),
      });
      const created = res?.data?.id ? res.data : (res?.id ? res : null);
      if (!created) throw new Error(t('common.error'));
      setGroups((prev) => [...prev, created]);
      onChange(created.id);
      success(t('clientGroup.created', { name: created.name || trimmed }));
      closeNew();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  const field = {
    width: '100%', minWidth: 0, padding: '0.6rem 0.7rem', borderRadius: 'var(--radius-md, 8px)',
    border: '1px solid var(--color-border, #e5e7eb)', background: 'var(--color-surface, #fff)',
    color: 'var(--color-text-primary)', fontSize: '1rem', minHeight: 44, boxSizing: 'border-box',
  };
  const label = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, fontSize: '0.85rem', fontWeight: 600 };
  const noGroups = !loading && groups.length === 0;

  return (
    <div style={{ display: 'grid', gap: 8, minWidth: 0 }} data-testid="group-picker">
      <label style={label}>
        {t('clientGroup.label')}
        <div style={{ display: 'flex', gap: 8, alignItems: 'stretch', minWidth: 0 }}>
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled || loading}
            style={{ ...field, flex: 1 }}
            data-testid="group-picker-select"
          >
            <option value="">{loading ? t('common.loading') : t('clientGroup.none')}</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {!showNew && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowNew(true)}
              disabled={disabled}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', flexShrink: 0 }}
              data-testid="group-picker-new"
            >
              <Plus size={16} /> {t('clientGroup.newGroup')}
            </button>
          )}
        </div>
      </label>

      {!showNew && (hint || noGroups) && (
        <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
          {noGroups ? t('clientGroup.noGroupsHint') : hint}
        </div>
      )}

      {showNew && (
        <div
          style={{
            display: 'grid', gap: 10, padding: 12, borderRadius: 10, minWidth: 0,
            border: '1px dashed var(--color-border, #e5e7eb)', background: 'var(--color-surface-alt, transparent)',
          }}
          data-testid="group-picker-new-form"
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <strong style={{ fontSize: '0.9rem' }}>{t('clientGroup.newGroupTitle')}</strong>
            <button
              type="button"
              onClick={closeNew}
              aria-label={t('common.cancel')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'inline-flex' }}
            >
              <X size={18} />
            </button>
          </div>
          <label style={label}>
            {t('clientGroup.name')}
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              // Enter would submit the surrounding add-client form instead.
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); create(); } }}
              placeholder={t('clientGroup.namePlaceholder')}
              style={field}
              autoFocus
              data-testid="group-picker-name"
            />
          </label>
          <label style={label}>
            {coachOptional ? t('clientGroup.coach') : t('clientGroup.teacher')}
            <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} style={field} data-testid="group-picker-teacher">
              <option value="">{coachOptional ? t('clientGroup.noCoach') : t('clientGroup.chooseTeacher')}</option>
              {teachers.map((tr) => (
                <option key={tr.id} value={tr.id}>{[tr.firstName, tr.lastName].filter(Boolean).join(' ') || tr.name}</option>
              ))}
            </select>
          </label>
          {error && <div role="alert" style={{ color: 'var(--danger-600, #dc2626)', fontSize: '0.85rem' }}>{error}</div>}
          <button
            type="button"
            className="btn btn-primary"
            onClick={create}
            disabled={creating}
            data-testid="group-picker-create"
          >
            {creating ? t('clientGroup.creating') : t('clientGroup.create')}
          </button>
        </div>
      )}
    </div>
  );
}
